/* External */
import {
  memo,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { addHours } from 'date-fns';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Components */
import FocusableButton from '../../../components/FocusableButton';
import DetailContainer from '../../../components/DetailContainer';
import LiveStartTime from '../../../components/LiveStartTime';
import LiveProgressBar from '../../../components/LiveProgressBar';
import LiveTimeLeft from '../../../components/LiveTimeLeft';

/* Hooks */
import useFetchContent from '../../../hooks/useFetchContent';
import useChannel from '../../../hooks/useChannel';
import useCurrentAndNextSchedulesAndFetch from '../../../hooks/useCurrentAndNextSchedulesAndFetch';
import useRedirectToPlayer from '../../../hooks/useRedirectToPlayer';

/* Utils */
import { DOT_SEPARATOR, REPORT_GA } from '../../../utils/constants';
import { noOp } from '../../../utils/functions';
import { redirectTo } from '../../../utils/history';
import { getLink, Routes } from '../../../utils/router';
import { useOnBackPress } from '../../../utils/keyDownEvents';
import {
  getRating,
  useTitle,
  isPlayingNow,
  joinArrayWithSpace,
} from '../../../utils/metaDataUtils';
import { PLAY_SOURCE_PAGE } from '../../../utils/trackSourceConviva';

/* Providers */
import { useCurrentTime } from '../../../providers/CurrentTimeProvider';
import { useLoading } from '../../../providers/LoadingProvider';
import { useInterstitialInfo } from '../../../providers/InterstitialProvider';

/* Services */
import { gaService } from '../../../services/gaService';

/* Styles */
import './Interstitial.scss';

// TODO: This code needs to be refactored, it has almost the same logic as DetailLive
const requiredProps = ['title', 'description', 'rating', 'images'];
function Interstitial({ setFocus, realFocusKey }) {
  const {
    interstitial,
    handleInterstitialSeen,
  } = useInterstitialInfo();
  const { channelId, showStartTime } = interstitial || {};
  const showEndTime = showStartTime && addHours(new Date(showStartTime), 5);
  const channel = useChannel(channelId);
  const { currentAndNextSchedules } = useCurrentAndNextSchedulesAndFetch(
    channel,
    1,
    showStartTime && new Date(showStartTime),
    showEndTime,
    true,
  );
  const schedule = currentAndNextSchedules?.first();
  const { content, isError } = useFetchContent({
    contentId: schedule?.content,
    requiredProps,
    sourceProvider: schedule?.sourceProvider,
  });
  const watchNowButtonFocusKey = `${realFocusKey}-watch-now`;
  const { hideLoading } = useLoading();

  const { startTime, endTime } = schedule || {};

  const { description, vrioAssetId, images } = content || {};

  const currentTime = useCurrentTime();

  const isPlayingNowFlag = useMemo(
    () => isPlayingNow(schedule, new Date(currentTime * 60_000)),
    [currentTime, schedule],
  );

  const buttonLiveText = isPlayingNowFlag ? 'common.watchNow' : 'live.seeChannel';

  const query = useMemo(() => ({
    fromInterstitial: true,
    interstitialContentId: vrioAssetId,
  }), [vrioAssetId]);

  const title = useTitle(content);

  const redirectToPlayerStation = useRedirectToPlayer(channel, {
    page: PLAY_SOURCE_PAGE.INTERSTITIAL,
    section: title || '',
    query,
    state: {
      transmissionType: channel?.schema || content?.type || content?.schema,
      sectionName: 'interstitial',
      componentType: 'interstitial',
      collectionName: channel?.title || content?.title,
    },
  });

  const handleOnEnterPressWatchNow = useCallback(() => {
    handleInterstitialSeen();
    redirectToPlayerStation();
  }, [redirectToPlayerStation, handleInterstitialSeen]);

  const handleOnEnterPressGoHome = useCallback(() => {
    gaService.sendEvent({
      action: REPORT_GA.GA_EVENT_ACTION_CLOSE,
      label: REPORT_GA.GA_EVENT_LABEL_NA,
      category: REPORT_GA.GA_EVENT_CATEGORY_INTERSTITIAL,
    });
    handleInterstitialSeen();
    redirectTo(getLink(Routes.Private));
  }, [handleInterstitialSeen]);

  const subtitle = useMemo(() => joinArrayWithSpace([
    getRating(content),
    (
      <div>
        <LiveTimeLeft
          startDate={startTime}
          endDate={endTime}
          stationName={channel?.title}
          separator={DOT_SEPARATOR}
        />
        {channel?.title}
      </div>
    ),
  ]), [endTime, content, startTime, channel]);

  const dataImage = {
    images,
  };

  useEffect(() => {
    if (content) {
      hideLoading();
      setFocus(watchNowButtonFocusKey);
    }
  }, [hideLoading, setFocus, watchNowButtonFocusKey, content]);

  useOnBackPress({ listener: noOp });

  useEffect(() => {
    if (isError) {
      redirectTo(getLink(Routes.Private.Home));
    }
  }, [isError, schedule]);

  return (
    !!content && (
      <DetailContainer
        content={dataImage}
        className="dtv-html5-c-interstitial"
      >
        <LiveStartTime
          startDate={startTime}
          endDate={endTime}
          className="dtv-html5-c-interstitial__live-start"
        />
        <DetailContainer.Title className="dtv-html5-c-interstitial__title">
          {title || ''}
        </DetailContainer.Title>

        <DetailContainer.Subtitle className="dtv-html5-c-interstitial__subtitle">
          {subtitle}
        </DetailContainer.Subtitle>
        <DetailContainer.Description className="dtv-html5-c-interstitial__description">
          {description || ''}
        </DetailContainer.Description>
        <div className="dtv-html5-c-interstitial__content-buttons">
          <FocusableButton
            focusKey={watchNowButtonFocusKey}
            icon="play"
            primary
            onEnterPress={handleOnEnterPressWatchNow}
            className="dtv-html5-c-interstitial__button-watch-now"
          >
            <FormattedMessage id={buttonLiveText} />
          </FocusableButton>
          <FocusableButton
            icon="logo-dtv"
            primary
            onEnterPress={handleOnEnterPressGoHome}
          >
            <FormattedMessage id="interstitial.goToDirectv" />
          </FocusableButton>
        </div>
        <div className="dtv-html5-c-interstitial__progress-bar">
          <LiveProgressBar startDate={startTime} endDate={endTime} className="dtv-html5-c-interstitial__progress-bar" />
        </div>
      </DetailContainer>
    )
  );
}

Interstitial.propTypes = {
  setFocus: PropTypes.func.isRequired,
  realFocusKey: PropTypes.string.isRequired,
};

export default memo(withFocusable()(memo(Interstitial)));
